import React, { useMemo, useState } from "react";
import { MessengerPreviewPanel } from "./MessengerPreviewPanel";

type TemplateParams = {
    view_order_link: string;
    set_location_link: string;
    qr_code_link: string;
    total_amount: string | number;
    delivery_fee: string | number;
    total_items: string | number;
    customer_name: string;
    product_code: string;
    current_date: string;
    order_number: string;
};

type TemplateModel = {
    templateCode: string;
    title: string;
    content: string; // supports {{var}} + [[link:Text|url]] + [[postback:Text|payload]] + [[qr:url]]
};

type RenderAction =
    | { type: "link"; text: string; url: string }
    | { type: "postback"; text: string; payload: string };

type TemplateEditorProps = {
    initial?: Partial<TemplateModel>;
    params?: Partial<TemplateParams>; // allow partial; we'll fill sample defaults
    onSave?: (tpl: TemplateModel) => void;

    // Called when user clicks a postback button inside preview
    onPostback?: (payload: string) => void;
};

const AVAILABLE_PARAMS: Array<keyof TemplateParams> = [
    "view_order_link",
    "set_location_link",
    "qr_code_link",
    "total_amount",
    "delivery_fee",
    "total_items",
    "customer_name",
    "product_code",
    "current_date",
    "order_number",
];

// --- Simple helpers ---
function replaceVariables(input: string, params: Record<string, any>) {
    return input.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
        const v = params[key];
        return v === undefined || v === null ? `{{${key}}}` : String(v);
    });
}

/**
 * Supported blocks inside content:
 *  - [[link:Text|URL]]
 *  - [[postback:Text|PAYLOAD]]
 *  - [[qr:URL]]  (renders <img/>)
 *
 * Everything else becomes text (with line breaks).
 */
function parseContentToBlocks(
    content: string,
    resolved: string
): Array<
    | { type: "text"; text: string }
    | { type: "actions"; actions: RenderAction[] }
    | { type: "qr"; url: string }
> {
    // We'll parse from the ORIGINAL content to keep markup, but
    // use "resolved" to fill variables. Easiest: parse on resolved string.
    const s = resolved;

    // Tokenize by our [[...]] patterns
    const regex = /\[\[\s*(link|postback|qr)\s*:\s*([^\]]+?)\s*\]\]/g;

    const blocks: Array<any> = [];
    let lastIndex = 0;

    // We'll collect actions in groups when they appear consecutively
    let pendingActions: RenderAction[] = [];

    const pushPendingActionsIfAny = () => {
        if (pendingActions.length) {
            blocks.push({ type: "actions", actions: pendingActions });
            pendingActions = [];
        }
    };

    let m: RegExpExecArray | null;
    while ((m = regex.exec(s)) !== null) {
        const [full, kind, body] = m;
        const start = m.index;
        const end = start + full.length;

        // Text before the token
        const before = s.slice(lastIndex, start);
        if (before) {
            pushPendingActionsIfAny();
            blocks.push({ type: "text", text: before });
        }

        if (kind === "qr") {
            pushPendingActionsIfAny();
            // body could be URL or already resolved variable
            const url = body.trim();
            blocks.push({ type: "qr", url });
        } else if (kind === "link" || kind === "postback") {
            // body format: Text|Target
            const parts = body.split("|");
            const text = (parts[0] ?? "").trim();
            const target = (parts[1] ?? "").trim();

            if (kind === "link") {
                pendingActions.push({ type: "link", text, url: target });
            } else {
                pendingActions.push({ type: "postback", text, payload: target });
            }
        }

        lastIndex = end;
    }

    const tail = s.slice(lastIndex);
    if (tail) {
        pushPendingActionsIfAny();
        blocks.push({ type: "text", text: tail });
    }
    pushPendingActionsIfAny();

    return blocks;
}

function joinWithCurrentCursor(textarea: HTMLTextAreaElement, insert: string) {
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    const next = textarea.value.slice(0, start) + insert + textarea.value.slice(end);
    const nextCursor = start + insert.length;
    return { next, nextCursor };
}

export default function MessageTemplateEditor({
    initial,
    params,
    onSave,
    onPostback,
}: TemplateEditorProps) {
    const sampleParams: TemplateParams = {
        view_order_link: "https://example.com/orders/ORD-2025-001",
        set_location_link: "https://maps.google.com/?q=11.5564,104.9282",
        qr_code_link: "https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=ORD-2025-001",
        total_amount: 12.5,
        delivery_fee: 1.0,
        total_items: 3,
        customer_name: "Sok Dara",
        product_code: "SKU-TSHIRT-RED-M",
        current_date: new Date().toLocaleDateString(),
        order_number: "ORD-2025-001",
        ...params,
    };

    const [model, setModel] = useState<TemplateModel>({
        templateCode: initial?.templateCode ?? "ORDER_CONFIRMATION",
        title: initial?.title ?? "Order Confirmation",
        content:
            initial?.content ??
            [
                "Hi {{customer_name}},",
                "",
                "Thanks for your order #{{order_number}} on {{current_date}}.",
                "Items: {{total_items}}",
                "Delivery fee: ${{delivery_fee}}",
                "Total: ${{total_amount}}",
                "",
                "Product code: {{product_code}}",
                "",
                "[[link:View Order|{{view_order_link}}]] [[link:Set Location|{{set_location_link}}]]",
                "[[postback:Confirm Order|CONFIRM_ORDER]] [[postback:Cancel|CANCEL_ORDER]]",
                "",
                "Scan QR:",
                "[[qr:{{qr_code_link}}]]",
            ].join("\n"),
    });

    const resolvedTitle = useMemo(
        () => replaceVariables(model.title, sampleParams as any),
        [model.title, sampleParams]
    );

    const resolvedContent = useMemo(
        () => replaceVariables(model.content, sampleParams as any),
        [model.content, sampleParams]
    );

    const previewBlocks = useMemo(
        () => parseContentToBlocks(model.content, resolvedContent),
        [model.content, resolvedContent]
    );

    const handleInsertVar = (k: string) => {
        const ta = document.getElementById("tpl-content") as HTMLTextAreaElement | null;
        if (!ta) {
            setModel((m) => ({ ...m, content: m.content + `{{${k}}}` }));
            return;
        }
        const { next, nextCursor } = joinWithCurrentCursor(ta, `{{${k}}}`);
        setModel((m) => ({ ...m, content: next }));
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(nextCursor, nextCursor);
        });
    };

    const handleInsertLinkBtn = () => {
        const ta = document.getElementById("tpl-content") as HTMLTextAreaElement | null;
        const insert = `[[link:View Order|{{view_order_link}}]]`;
        if (!ta) {
            setModel((m) => ({ ...m, content: m.content + insert }));
            return;
        }
        const { next, nextCursor } = joinWithCurrentCursor(ta, insert);
        setModel((m) => ({ ...m, content: next }));
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(nextCursor, nextCursor);
        });
    };

    const handleInsertPostbackBtn = () => {
        const ta = document.getElementById("tpl-content") as HTMLTextAreaElement | null;
        const insert = `[[postback:Confirm Order|CONFIRM_ORDER]]`;
        if (!ta) {
            setModel((m) => ({ ...m, content: m.content + insert }));
            return;
        }
        const { next, nextCursor } = joinWithCurrentCursor(ta, insert);
        setModel((m) => ({ ...m, content: next }));
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(nextCursor, nextCursor);
        });
    };

    const handleInsertQr = () => {
        const ta = document.getElementById("tpl-content") as HTMLTextAreaElement | null;
        const insert = `[[qr:{{qr_code_link}}]]`;
        if (!ta) {
            setModel((m) => ({ ...m, content: m.content + insert }));
            return;
        }
        const { next, nextCursor } = joinWithCurrentCursor(ta, insert);
        setModel((m) => ({ ...m, content: next }));
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(nextCursor, nextCursor);
        });
    };

    const save = () => {
        const payload: TemplateModel = {
            templateCode: model.templateCode.trim(),
            title: model.title,
            content: model.content,
        };
        onSave?.(payload);
        alert("Saved (hook onSave to persist in DB).");
    };

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* LEFT: Editor */}
            <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <h3 style={{ margin: 0 }}>Template Editor</h3>
                    <button
                        type="button"
                        onClick={save}
                        style={{
                            padding: "8px 12px",
                            borderRadius: 10,
                            border: "1px solid #111",
                            background: "#111",
                            color: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        Save
                    </button>
                </div>

                <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontSize: 12, color: "#555" }}>Template Code</span>
                        <input
                            value={model.templateCode}
                            onChange={(e) => setModel((m) => ({ ...m, templateCode: e.target.value }))}
                            style={{
                                padding: "10px 12px",
                                borderRadius: 10,
                                border: "1px solid #ddd",
                            }}
                            placeholder="e.g. ORDER_CONFIRMATION"
                        />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontSize: 12, color: "#555" }}>Title</span>
                        <input
                            value={model.title}
                            onChange={(e) => setModel((m) => ({ ...m, title: e.target.value }))}
                            style={{
                                padding: "10px 12px",
                                borderRadius: 10,
                                border: "1px solid #ddd",
                            }}
                            placeholder="e.g. Order Confirmation"
                        />
                    </label>

                    <div style={{ display: "grid", gap: 8 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 12, color: "#555" }}>Insert:</span>

                            {AVAILABLE_PARAMS.map((k) => (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={() => handleInsertVar(k)}
                                    style={{
                                        padding: "6px 10px",
                                        borderRadius: 999,
                                        border: "1px solid #ddd",
                                        background: "#fff",
                                        cursor: "pointer",
                                        fontSize: 12,
                                    }}
                                    title={`Insert {{${k}}}`}
                                >
                                    {`{{${k}}}`}
                                </button>
                            ))}

                            <span style={{ width: 1, height: 20, background: "#eee", margin: "0 4px" }} />

                            <button
                                type="button"
                                onClick={handleInsertLinkBtn}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: 999,
                                    border: "1px solid #ddd",
                                    background: "#fff",
                                    cursor: "pointer",
                                    fontSize: 12,
                                }}
                                title="Insert link button markup"
                            >
                                + Link Button
                            </button>

                            <button
                                type="button"
                                onClick={handleInsertPostbackBtn}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: 999,
                                    border: "1px solid #ddd",
                                    background: "#fff",
                                    cursor: "pointer",
                                    fontSize: 12,
                                }}
                                title="Insert postback button markup"
                            >
                                + Postback Button
                            </button>

                            <button
                                type="button"
                                onClick={handleInsertQr}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: 999,
                                    border: "1px solid #ddd",
                                    background: "#fff",
                                    cursor: "pointer",
                                    fontSize: 12,
                                }}
                                title="Insert QR image markup"
                            >
                                + QR
                            </button>
                        </div>

                        <label style={{ display: "grid", gap: 6 }}>
                            <span style={{ fontSize: 12, color: "#555" }}>Content</span>
                            <textarea
                                id="tpl-content"
                                value={model.content}
                                onChange={(e) => setModel((m) => ({ ...m, content: e.target.value }))}
                                rows={18}
                                style={{
                                    padding: "12px",
                                    borderRadius: 10,
                                    border: "1px solid #ddd",
                                    width: "100%",
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                    fontSize: 13,
                                    lineHeight: 1.45,
                                }}
                                placeholder={`Use {{variables}} and markup like:
[[link:View Order|{{view_order_link}}]]
[[postback:Confirm|CONFIRM_ORDER]]
[[qr:{{qr_code_link}}]]
`}
                            />
                        </label>

                        <div style={{ fontSize: 12, color: "#666", lineHeight: 1.4 }}>
                            <div><b>Markup supported:</b></div>
                            <div><code>[[link:Button Text|URL]]</code></div>
                            <div><code>[[postback:Button Text|PAYLOAD]]</code></div>
                            <div><code>[[qr:URL]]</code></div>
                            <div style={{ marginTop: 6 }}>
                                Variables are <code>{``}</code> (unknown vars stay as-is).
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT: Preview */}
            <MessengerPreviewPanel
                resolvedTitle={resolvedTitle}
                previewBlocks={previewBlocks as any}
                AVAILABLE_PARAMS={AVAILABLE_PARAMS as any}
                sampleParams={sampleParams as any}
                onPostback={onPostback}
                showParams={true}
            />

        </div>
    );
}